export type ScriptConfig = {
    /**
     * The full path to the script to load.
     */
    src: string;

    /**
     * An id that will be passed to the script's "id" attribute.
     */
    scriptId: string;

    /**
     * An optional document node to insert the script to. Defaults to `document.head`.
     */
    documentNode?: HTMLElement;
};

/**
 * Loads an external script and inserts it into the DOM.
 *
 * @param config - The script source, DOM id, and optional target node for the injected script tag.
 * @returns A promise that resolves once the script already exists or the injected script fires `load`.
 */
export const loadScript = (config: ScriptConfig): Promise<void> => {
    return new Promise(resolve => {
        const existingScript = document.getElementById(config.scriptId);

        if (existingScript) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = config.src;
        script.id = config.scriptId;
        script.onload = () => {
            resolve();
        };

        const node = config.documentNode ?? document.head;

        if (node) {
            node.appendChild(script);
        }
    });
};
